import { openSupportChat } from "../services/supportChat";
import React, { FC, ReactNode, useEffect, useState, useRef } from "react";
import { useForm, SubmitHandler, Controller, DefaultValues } from "react-hook-form";
import { css, styled } from "@mui/system";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { AccountSettings, Organization } from "../types/organizations";
import { AppUser, ProfileType } from "../types/users";
import { organizationAtom, signedInUserAtom } from "../state/atoms";
import {
  getOutreachAuthUrl,
  convertDbAccountToAppAccount,
  getDbUser,
  getMyOrganization,
  updateOrganizationSettings,
  signUserOut,
  convertDbOrganizationSettingsToAppOrganizationSettings,
  convertAppOrganizationSettingsToDbOrganizationSettings,
  subToEmailsInQueue,
  subToRateLimit,
  RateLimitInfo,
  getOrgWebhookStatus,
  disconnectOutreach,
  disconnectHubSpot,
  getOutreachUsers,
  getHubSpotUsers,
  getHubSpotContactProperties,
  HubSpotContactProperty,
  getHubSpotLeadStatuses,
  HubSpotLeadStatus,
  getOutreachProspectCustomFields,
  OutreachCustomField,
  createHubSpotContactProperty,
} from "../services/firebase";
import CreateHubSpotPropertyDialog, { HubSpotFieldType } from "../components/Display/CreateHubSpotPropertyDialog";
import ExtensionSection from "../components/Extension/ExtensionSection";
import LiveVerificationsCard from "../components/HubSpotUi/LiveVerificationsCard";
import LiveVerificationsPanel from "../components/Insights/LiveVerificationsPanel";
import DialerSection from "../components/Dialer/DialerSection";
import MyDialerSection from "../components/Dialer/MyDialerSection";
import AddonsSection from "../components/Addons/AddonsSection";
import PhoneEnrichmentSection from "../components/Addons/PhoneEnrichmentSection";
import ExportSection from "../components/Addons/ExportSection";
import ExportHistorySection from "../components/History/ExportHistorySection";
import JobChangeSettings from "../components/Settings/JobChangeSettings";
import ConnectedAiApps from "../components/Settings/ConnectedAiApps";
import ProductLicenses from "../components/Addons/ProductLicenses";
import BillingSection from "../components/Billing/BillingSection";
import MeteredUsageSection from "../components/Billing/MeteredUsageSection";
import AddLicensesDialog from "../components/Billing/AddLicensesDialog";
import PlanUpgradePrompt from "../components/Billing/PlanUpgradePrompt";
import { orgPlanLocks } from "../common/plans";
import { useConnectOutreach } from "../common/hooks/useConnectOutreach";
import { useConnectHubSpot } from "../common/hooks/useConnectHubSpot";
import { useTheme } from "../common/contexts/ThemeContext";
import { useIsHubSpotCrm } from "../components/CrmUi/useCrm";
import { HubSpotSettingsPage, hubSpotSettingsLabel } from "../components/Layout/HubSpotAppShell";
import { OuPrimaryButton as HsPrimaryButton, OuSecondaryButton as HsSecondaryButton } from "../components/HubSpotUi";
import Card from "../components/Display/Card";
import { BlockBone, TextBone } from "../components/Display/Skeletons";
import Button, {
  ButtonSizes,
  ButtonVariants,
} from "../components/Display/Button";
import Text, { TextVariant } from "../components/Display/Text";
import Select from "../components/DataInput/Select";
import SearchableSelect, { SearchableSelectOption } from "../components/DataInput/SearchableSelect";
import Switch from "../components/DataInput/Switch";
import Input from "../components/DataInput/Input";
import {
  SettingsCaption,
  SettingsControlField,
  SettingsFooter,
  SettingsGroup,
  SettingsHeader,
  SettingsPanel,
  SettingsRow,
} from "../components/Settings";
import { FieldMappingRow, FieldMappingTable, MappingSystem } from "../components/Settings/FieldMapping";
import { HUBSPOT_SYSTEM, HubSpotMark, OUTREACH_SYSTEM, OutreachMark } from "../components/Settings/systems";
import { COLORS, FONT_FAMILIES, FONT_WEIGHTS, GAPS, RADII, SPACINGS } from "../common/consts";
import { devLog, devWarn } from "../common/utils/devLog";
import { removeUndefinedDeep } from "../common/utils/data";
import { CircleSvg } from "../components/Display/Shapes";
import { ReactComponent as BoringIcon } from "../assets/icons/boring-icon.svg";
import LinearProgress from "@mui/material/LinearProgress";
import LinkedInAutomationTab from "../components/Automation/LinkedInAutomationTab";

interface ShapeColorProps {
  color: string;
}

const ShapeColor = styled("div")<ShapeColorProps>`
  ${({ color }) => css`
    color: ${color};
    transition: color 0.15s ease-in-out;
    margin-bottom: ${RADII.xs};
  `};
`;

const SettingsCard = styled(Card)`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 4rem);
  max-height: calc(100vh - 4rem);
  padding: 0;
  overflow: hidden;
  position: relative;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

/* The page's own Save bar, pinned under the scrolling content. Every button
   sits at the right edge: Sign out, then Cancel and Save once there are edits. */
const PageFooter = styled(SettingsFooter)`
  flex-shrink: 0;
  background-color: var(--color-backgroundPrimary, ${COLORS.backgroundPrimary});
  border-top: 1px solid var(--color-borderPrimary, ${COLORS.borderPrimary});
  /* The right padding clears the chat bubble pinned to the window's corner. */
  padding: ${SPACINGS.sm} 6.5rem ${SPACINGS.sm} ${GAPS.lg};
  z-index: 10;

  @media (max-width: 768px) {
    padding: ${SPACINGS.sm} 6.5rem ${SPACINGS.sm} ${GAPS.md};
  }
`;

const SettingsContainer = styled("div")`
  display: flex;
  flex: 1;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const Sidebar = styled("div")`
  width: 200px;
  flex-shrink: 0;
  overflow-y: auto;
  background-color: var(--color-backgroundPrimary, ${COLORS.backgroundPrimary});
  padding-top: ${GAPS.lg};
  padding-left: ${GAPS.md};

  @media (max-width: 768px) {
    display: none;
  }
`;

const SidebarNavItem = styled("button")<{ active: boolean }>`
  width: calc(100% - ${GAPS.md});
  text-align: left;
  padding: ${SPACINGS.sm} ${SPACINGS.md};
  margin-bottom: ${SPACINGS.xs};
  border: none;
  background-color: ${({ active }) =>
    active ? `var(--color-backgroundBrandTab, ${COLORS.backgroundBrandTab})` : "transparent"};
  color: ${({ active }) =>
    active ? `var(--color-foregroundBrandLight, ${COLORS.foregroundBrandLight})` : `var(--color-foregroundBrand, ${COLORS.foregroundBrand})`};
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.875rem;
  font-weight: ${FONT_WEIGHTS.medium};
  line-height: 1.43;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-left: none;
  border-radius: 0.5rem;
  margin-left: ${GAPS.xs};

  &:hover {
    background-color: ${({ active }) =>
      active ? `var(--color-backgroundBrandTab, ${COLORS.backgroundBrandTab})` : `var(--color-backgroundBrandTabHover, ${COLORS.backgroundBrandTabHover})`};
    border-radius: 0.5rem;
  }

  /* HubSpot's menu rows (dropdown-menu-open.json): 40px, 8px 20px, 14/24 at
     400 in #333, #F0F0F0 behind the hovered and the current one. */
  html[data-crm="hubspot"] & {
    min-height: 40px;
    margin: 0 0 2px;
    padding: 8px 20px;
    border-radius: 6px;
    background-color: ${({ active }) => (active ? "var(--hs-fill-hover, #f0f0f0)" : "transparent")};
    color: var(--hs-text, #333333);
    font-size: 14px;
    font-weight: ${({ active }) => (active ? 500 : 400)};
    line-height: 24px;
    box-shadow: none;
    transition: background-color 150ms ease-out;
  }

  html[data-crm="hubspot"] &:hover {
    background-color: var(--hs-fill-hover, #f0f0f0);
    border-radius: 6px;
  }

  html[data-crm="hubspot"] &:focus-visible {
    outline: 2px solid var(--hs-focus, #016de1);
    outline-offset: -2px;
  }
`;

const ContentArea = styled("div")`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${GAPS.xl};
  padding-top: ${GAPS.lg};
  background-color: var(--color-backgroundPrimary, ${COLORS.backgroundPrimary});
  transition: background-color 0.3s ease;

  /* Outreach's fields: 36px tall, white in light mode, grey when read-only. */
  .MuiInputBase-root:not(.MuiInputBase-multiline) {
    min-height: 36px;
    height: 36px;
  }
  /* Doubled class: the Input component paints every field white at the same
     specificity, and its styles land after these. */
  && .MuiInputBase-root.Mui-disabled {
    background-color: var(--color-backgroundSecondary, rgb(246, 246, 247)) !important;
  }

  /* Outreach's field text: 14/20, so the value sits centred in the 36px box. */
  && .MuiOutlinedInput-input:not(.MuiSelect-select):not(.MuiAutocomplete-input) {
    line-height: 20px;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  /* Outreach's labels: regular 14px inside the field, 13px once it floats. */
  && .MuiFormControl-root .MuiFormLabel-root {
    font-family: ${FONT_FAMILIES.regular} !important;
    font-weight: ${FONT_WEIGHTS.regular} !important;
    font-size: 0.875rem !important;
    line-height: 1.25rem;
    transform: translate(14px, 8px) scale(1);
  }
  && .MuiFormControl-root .MuiFormLabel-root.MuiInputLabel-shrink {
    font-size: 0.8125rem !important;
    transform: translate(14px, -8px) scale(1);
  }

  /* One outline for every field: the searchable pickers drew a flat grey. */
  && .MuiInputBase-root:not(:hover):not(.Mui-focused):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline {
    border-color: var(--color-inputOutline, ${COLORS.inputOutline});
  }

  /* Outreach's focus ring: a 1px #7971FF outline. */
  && .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #7971ff !important;
    border-width: 1px !important;
  }

  @media (max-width: 768px) {
    padding: ${GAPS.md};
    padding-top: ${GAPS.md};
  }
`;

/* One sidebar section: a heading, then its parts stacked with no boxes, the
   way Outreach's Organization page stacks "Organization info" and the rest.
   Every section shares one centred column, so everything lines up. */
const SectionWrapper = styled("section")`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  max-width: 900px;
  margin: 0 auto 3.5rem;
  box-sizing: border-box;
`;

/* Outreach's section heading: Work Sans 600 at 16/24. */
const ContentHeader = styled(Text)`
  font-family: ${FONT_FAMILIES.titles};
  font-size: 1rem;
  line-height: 1.5rem;
  font-weight: ${FONT_WEIGHTS.semibold};
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
  margin: 0 0 -1rem;
`;

/* Fields laid out three to a row, one to a row on a phone. */
const FieldGrid = styled("div")`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${SPACINGS.xl} ${SPACINGS.lg};
  padding-top: ${SPACINGS.sm};

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const TwoFieldGrid = styled(FieldGrid)`
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

const IntegrationName = styled("span")`
  display: flex;
  align-items: center;
  gap: ${SPACINGS.sm};
  font-family: ${FONT_FAMILIES.titles};
  font-weight: ${FONT_WEIGHTS.medium};
  font-size: 1rem;
  line-height: 1.5;
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
`;

const IntegrationStatus = styled("span")`
  display: flex;
  align-items: center;
  gap: ${SPACINGS.xs};
`;

/* A section that is only fields: the panel's layout without its box. */
const FieldsSection = styled("section")`
  max-width: 900px;
  box-sizing: border-box;
`;

const PanelBody = styled("div")`
  display: flex;
  flex-direction: column;
  gap: ${SPACINGS.lg};
`;

const LicenseCount = styled("div")`
  font-family: ${FONT_FAMILIES.titles};
  font-weight: ${FONT_WEIGHTS.semibold};
  font-size: 1.75rem;
  line-height: 1;
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
`;

const LicenseUtilizationBar = styled(LinearProgress)`
  height: 8px;
  border-radius: ${RADII.md};
  background-color: var(--color-backgroundSecondary, ${COLORS.backgroundSecondary});

  .MuiLinearProgress-bar {
    background-color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
    border-radius: ${RADII.md};
  }
`;

const SettingsHeaderBar = styled("div")`
  padding: ${SPACINGS.xl} ${SPACINGS.lg} ${SPACINGS.xl} ${SPACINGS.xl};
  flex-shrink: 0;
  position: sticky;
  top: 0;
  background-color: var(--color-backgroundPrimary, ${COLORS.backgroundPrimary});
  z-index: 10;
  transition: background-color 0.3s ease;

  @media (max-width: 768px) {
    padding: ${SPACINGS.md} ${SPACINGS.md} !important;
  }
`;

const PageTitle = styled("h2")`
  margin: 0;
  font-family: ${FONT_FAMILIES.titles};
  font-size: 1.5rem;
  font-weight: 550;
  line-height: 1.5;
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
`;

const BoringBadge = styled("span")`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: ${RADII.full};
  background-color: rgb(94, 94, 175);
  color: #ffffff;

  && svg {
    width: 20px;
    height: 20px;
  }
`;

const BORING_SYSTEM: MappingSystem = {
  name: "boring",
  icon: (
    <BoringBadge>
      {/* The asset has no viewBox of its own, so give it one to scale by. */}
      <BoringIcon viewBox="0 0 57 57" />
    </BoringBadge>
  ),
  rgb: "94, 94, 175",
};

type SettingsSection = "account" | "licenses" | "billing" | "addons" | "workflows" | "integration-status" | "dialer" | "linkedin" | "history" | "ai-apps";

const navigationItems: { label: string; value: SettingsSection }[] = [
  { label: "Account", value: "account" },
  { label: "Licenses", value: "licenses" },
  { label: "Billing", value: "billing" },
  { label: "Add-ons", value: "addons" },
  { label: "Configuration", value: "workflows" },
  { label: "Integration", value: "integration-status" },
  { label: "Dialer", value: "dialer" },
  { label: "LinkedIn", value: "linkedin" },
  { label: "History", value: "history" },
  { label: "AI apps", value: "ai-apps" },
];

/**
 * The form's values for a user and their organization. Every reset builds
 * them here, so a setting added to the form cannot be left out of one reset,
 * read as off once the organization loads, and then saved as null.
 */
const toFormValues = (
  user: AppUser | null,
  organization: Organization | null
): DefaultValues<AccountSettings> => ({
  userSettings: {
    firstName: user?.firstName || "",
    title: user?.title || "",
    profile: user?.profile || ProfileType.DEFAULT,
    phoneNumber: user?.phoneNumber || "",
  },
  organizationSettings: {
    isCustomFieldEnabled: organization?.isCustomFieldEnabled === true,
    customField: organization?.customField ?? null,
    disableOptOutProspects: !organization?.disableOptOutProspects,
    isTimestampCustomFieldEnabled: organization?.isTimestampCustomFieldEnabled === true,
    timestampCustomField: organization?.timestampCustomField ?? null,
    isEnrichedCustomFieldEnabled: organization?.isEnrichedCustomFieldEnabled === true,
    enrichedCustomField: organization?.enrichedCustomField ?? null,
    isLinkedInUrnCustomFieldEnabled: organization?.isLinkedInUrnCustomFieldEnabled === true,
    linkedInUrnCustomField: organization?.linkedInUrnCustomField ?? null,
    isPhoneStatusCustomFieldEnabled: organization?.isPhoneStatusCustomFieldEnabled === true,
    phoneStatusCustomField: organization?.phoneStatusCustomField ?? null,
    isPhoneVerifiedAtCustomFieldEnabled: organization?.isPhoneVerifiedAtCustomFieldEnabled === true,
    phoneVerifiedAtCustomField: organization?.phoneVerifiedAtCustomField ?? null,
    // HubSpot: unset means on; Outreach: off until a field is picked.
    isJobChangeDateCustomFieldEnabled:
      organization?.initialIntegrationType === "hubspot"
        ? organization?.isJobChangeDateCustomFieldEnabled !== false
        : organization?.isJobChangeDateCustomFieldEnabled === true,
    jobChangeDateCustomField: organization?.jobChangeDateCustomField ?? null,
    isPhoneCarrierLookupEnabled: organization?.isPhoneCarrierLookupEnabled === true,
    // Unset means on.
    isAutoEnrichOnVisitEnabled: organization?.isAutoEnrichOnVisitEnabled !== false,
    // Unset means on.
    isLinkedInActivityLoggingEnabled: organization?.isLinkedInActivityLoggingEnabled !== false,
    isLinkedInActivityBackfillEnabled: organization?.isLinkedInActivityBackfillEnabled === true,
    // Unset means on.
    isStopSequencesOnLinkedInReplyEnabled: organization?.isStopSequencesOnLinkedInReplyEnabled !== false,
    // Unset means on.
    isBoringActivityLoggingEnabled: organization?.isBoringActivityLoggingEnabled !== false,
    // Empty means Contact owner (hubspot_owner_id).
    ownerProperty: organization?.ownerProperty || "",
    ownerChangeTriggersVerification: organization?.ownerChangeTriggersVerification === true,
    hubspotSequenceReenrollmentEnabled: organization?.hubspotSequenceReenrollmentEnabled === true,
    // availableLicenses is read-only, not part of form
  },
});

type MappingSwitchName =
  | "organizationSettings.isCustomFieldEnabled"
  | "organizationSettings.isTimestampCustomFieldEnabled"
  | "organizationSettings.isEnrichedCustomFieldEnabled"
  | "organizationSettings.isLinkedInUrnCustomFieldEnabled"
  | "organizationSettings.isPhoneStatusCustomFieldEnabled"
  | "organizationSettings.isPhoneVerifiedAtCustomFieldEnabled"
  | "organizationSettings.isJobChangeDateCustomFieldEnabled";

type MappingFieldName =
  | "organizationSettings.customField"
  | "organizationSettings.timestampCustomField"
  | "organizationSettings.enrichedCustomField"
  | "organizationSettings.linkedInUrnCustomField"
  | "organizationSettings.phoneStatusCustomField"
  | "organizationSettings.phoneVerifiedAtCustomField"
  | "organizationSettings.jobChangeDateCustomField";

/** One row of the field mapping: a boring value and where it is written. */
interface FieldMappingConfig {
  id: string;
  label: string;
  /** What it writes, under the name. */
  detail: string;
  /** The longer explanation, on hover. */
  hint: string;
  switchName: MappingSwitchName;
  fieldName: MappingFieldName;
  /** Outreach: sibling mappings whose custom field this one may not reuse. */
  excludes?: MappingFieldName[];
  /** HubSpot writes this one to a contact property. Without it the row is Outreach only. */
  hubSpot?: {
    target: "emailStatus" | "verificationDate" | "emailSource" | "jobChangeDate";
    fieldType: HubSpotFieldType;
    propertyLabel: string;
    /** Whether "+ Create new property" leads the list or follows the blank. */
    createFirst: boolean;
  };
}

interface SettingsProps {
  /** HubSpot orgs: the settings panel item to show, alone, as a HubSpot settings page. */
  hubspotSection?: string;
  /** Outreach layout: the section to scroll to on first render (the dashboard's ?open=billing). */
  initialSection?: "billing";
}

/* Each HubSpot settings page: its description, and its boxed tabs where it has parts. */
const HUBSPOT_PAGES: Record<string, { description: string; tabs?: { value: string; label: string }[] }> = {
  account: { description: "Your name and contact details in boring." },
  licenses: { description: "Choose which HubSpot teams get boring licenses, and keep them in sync automatically." },
  billing: {
    description: "Your plan, payment method and invoices, and what you've used this period.",
    tabs: [
      { value: "plan", label: "Plan & billing" },
      { value: "usage", label: "Usage" },
    ],
  },
  addons: {
    description: "Turn on extra boring products and choose how they run.",
    tabs: [
      { value: "addons", label: "Add-ons" },
      { value: "phone", label: "Phone enrichment" },
    ],
  },
  export: {
    description: "What happens when someone on your team exports a LinkedIn profile, and every export boring made.",
    tabs: [
      { value: "settings", label: "Export" },
      { value: "history", label: "History" },
    ],
  },
  "integration-status": { description: "The HubSpot account boring reads contacts from and writes verdicts to, your boring Chrome extension, and what boring is checking right now." },
  workflows: {
    description: "What boring does on its own when it checks a contact, the HubSpot properties it writes to, and what happens when someone changes jobs.",
    tabs: [
      { value: "automation", label: "Automation" },
      { value: "mapping", label: "Field mapping" },
      { value: "jobChanges", label: "Job changes" },
    ],
  },
  dialer: {
    description: "How boring dials from HubSpot's task queue, for you and for your team.",
    tabs: [
      { value: "mine", label: "My dialer" },
      { value: "team", label: "Team dialer" },
    ],
  },
  linkedin: { description: "LinkedIn automation: pacing, limits and what boring sends." },
  "ai-apps": { description: "AI apps like Claude and ChatGPT connected to boring, and who connected them." },
};

const Settings: FC<SettingsProps> = ({ hubspotSection, initialSection }) => {
  const [hubspotTab, setHubspotTab] = useState<Record<string, string>>({});
  const [signedInUser, setSignedInUser] = useRecoilState(signedInUserAtom);
  const [organization, setOrganization] = useRecoilState(organizationAtom);
  const [loading, setLoading] = useState(false);
  const [isAddLicensesOpen, setIsAddLicensesOpen] = useState(false);
  const [isIntegrationChecked, setIsIntegrationChecked] = useState<
    boolean | undefined
  >(undefined);
  const [isIntegrationUpdateLoading, setIsIntegrationUpdateLoading] =
    useState(false);
  const [emailsInQueue, setEmailsInQueue] = useState(0);
  const [rateLimit, setRateLimit] = useState<RateLimitInfo | null>(null);
  const [activeSection, setActiveSection] = useState<SettingsSection>("account");
  const [availableTeams, setAvailableTeams] = useState<string[]>([]);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [isLoadingTeamsRoles, setIsLoadingTeamsRoles] = useState(false);
  const [syncTeams, setSyncTeams] = useState<string[]>([]);
  const [syncRoles, setSyncRoles] = useState<string[]>([]);
  const [originalSyncTeams, setOriginalSyncTeams] = useState<string[]>([]);
  const [originalSyncRoles, setOriginalSyncRoles] = useState<string[]>([]);
  const [autoSyncLicensesEnabled, setAutoSyncLicensesEnabled] = useState<boolean>(false);
  const [originalAutoSyncLicensesEnabled, setOriginalAutoSyncLicensesEnabled] = useState<boolean>(false);
  const [outreachUsers, setOutreachUsers] = useState<any[]>([]);
  const [isLoadingLicenseUsers, setIsLoadingLicenseUsers] = useState(false);
  const [hubspotPropertiesRaw, setHubspotProperties] = useState<HubSpotContactProperty[]>([]);
  const hubspotProperties = Array.isArray(hubspotPropertiesRaw) ? hubspotPropertiesRaw : [];
  const [isLoadingHubspotProperties, setIsLoadingHubspotProperties] = useState(false);
  const [outreachCustomFieldsRaw, setOutreachCustomFields] = useState<OutreachCustomField[]>([]);
  const outreachCustomFields = Array.isArray(outreachCustomFieldsRaw) ? outreachCustomFieldsRaw : [];
  const [isLoadingOutreachFields, setIsLoadingOutreachFields] = useState(false);
  const [hubspotLeadStatusesRaw, setHubspotLeadStatuses] = useState<HubSpotLeadStatus[]>([]);
  const hubspotLeadStatuses = Array.isArray(hubspotLeadStatusesRaw) ? hubspotLeadStatusesRaw : [];
  const [isLoadingLeadStatuses, setIsLoadingLeadStatuses] = useState(false);
  const [invalidEmailLeadStatus, setInvalidEmailLeadStatus] = useState<string>(organization?.invalidEmailLeadStatus || "");
  const [originalInvalidEmailLeadStatus, setOriginalInvalidEmailLeadStatus] = useState<string>(organization?.invalidEmailLeadStatus || "");
  const [validEmailLeadStatus, setValidEmailLeadStatus] = useState<string>(organization?.validEmailLeadStatus || "");
  const [originalValidEmailLeadStatus, setOriginalValidEmailLeadStatus] = useState<string>(organization?.validEmailLeadStatus || "");
  
  // HubSpot property creation dialog state
  const [createPropertyDialogOpen, setCreatePropertyDialogOpen] = useState(false);
  const [createPropertyFieldType, setCreatePropertyFieldType] = useState<HubSpotFieldType>('text');
  const [createPropertyLabel, setCreatePropertyLabel] = useState('');
  const [createPropertyTarget, setCreatePropertyTarget] = useState<'emailStatus' | 'verificationDate' | 'emailSource' | 'jobChangeDate'>('emailStatus');
  const [isCreatingProperty, setIsCreatingProperty] = useState(false);
  
  const { openOutreachAuthUrl, isProcessing: isAuthUrlLoading } =
    useConnectOutreach();
  const { openHubSpotAuthUrl, isProcessing: isHubSpotAuthUrlLoading } =
    useConnectHubSpot();
  const { mode, setMode } = useTheme();
  const isHubSpotLook = useIsHubSpotCrm();

  // Use organization as source of truth for auth (same as Dashboard). User doc often lacks these.
  // Use isOutreachAuthorized flag as source of truth.
  // Don't fall back to !!outreachAuthentication — tokens may exist from a
  // previous session while the flag was explicitly set to false on disconnect.
  const isOutreachAuthorized =
    signedInUser?.isOutreachAuthorized ||
    organization?.isOutreachAuthorized === true;
  const isHubSpotAuthorized =
    signedInUser?.isHubSpotAuthorized ||
    organization?.isHubSpotAuthorized === true ||
    !!organization?.hubSpotAuthentication;

  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    setValue,
    formState: { isDirty },
  } = useForm<AccountSettings>({
    defaultValues: toFormValues(signedInUser, organization),
  });
  // Form submission handler
  const onSubmit: SubmitHandler<AccountSettings> = (data) => {
    handleFormSubmit(data);
  };

  const customFieldValue = watch(
    "organizationSettings.customField"
  );
  const timestampCustomFieldValue = watch(
    "organizationSettings.timestampCustomField"
  );
  const enrichedCustomFieldValue = watch(
    "organizationSettings.enrichedCustomField"
  );

  const isCustomFieldEnabled = watch(
    "organizationSettings.isCustomFieldEnabled"
  );
  const isLinkedInActivityLoggingEnabled = watch(
    "organizationSettings.isLinkedInActivityLoggingEnabled"
  );
  const isTimestampCustomFieldEnabled = watch(
    "organizationSettings.isTimestampCustomFieldEnabled"
  );
  const isEnrichedCustomFieldEnabled = watch(
    "organizationSettings.isEnrichedCustomFieldEnabled"
  );
  const isLinkedInUrnCustomFieldEnabled = watch(
    "organizationSettings.isLinkedInUrnCustomFieldEnabled"
  );
  const linkedInUrnCustomFieldValue = watch(
    "organizationSettings.linkedInUrnCustomField"
  );
  const isPhoneStatusCustomFieldEnabled = watch(
    "organizationSettings.isPhoneStatusCustomFieldEnabled"
  );
  const phoneStatusCustomFieldValue = watch(
    "organizationSettings.phoneStatusCustomField"
  );
  const isPhoneVerifiedAtCustomFieldEnabled = watch(
    "organizationSettings.isPhoneVerifiedAtCustomFieldEnabled"
  );
  const phoneVerifiedAtCustomFieldValue = watch(
    "organizationSettings.phoneVerifiedAtCustomField"
  );
  const isJobChangeDateCustomFieldEnabled = watch(
    "organizationSettings.isJobChangeDateCustomFieldEnabled"
  );
  const jobChangeDateCustomFieldValue = watch(
    "organizationSettings.jobChangeDateCustomField"
  );

  // For Outreach, convert to numbers; for HubSpot, keep as strings
  const isHubSpot = organization?.initialIntegrationType === "hubspot";
  const timestampCustomFieldNumberValue = isHubSpot
    ? timestampCustomFieldValue // HubSpot: keep as string
    : (Number(timestampCustomFieldValue) || null); // Outreach: convert to number
  const enrichedCustomFieldNumberValue = isHubSpot
    ? enrichedCustomFieldValue // HubSpot: keep as string
    : (Number(enrichedCustomFieldValue) || null); // Outreach: convert to number

  // Check if sync config has changed
  const syncConfigHasChanged = 
    JSON.stringify(syncTeams.sort()) !== JSON.stringify(originalSyncTeams.sort()) ||
    JSON.stringify(syncRoles.sort()) !== JSON.stringify(originalSyncRoles.sort()) ||
    autoSyncLicensesEnabled !== originalAutoSyncLicensesEnabled;

  // Check if either lead status has changed
  const leadStatusHasChanged =
    invalidEmailLeadStatus !== originalInvalidEmailLeadStatus ||
    validEmailLeadStatus !== originalValidEmailLeadStatus;

  // Save button should be enabled if form is dirty OR sync config has changed OR lead status has changed
  const hasUnsavedChanges = isDirty || syncConfigHasChanged || leadStatusHasChanged;

  const handleCancel = () => {
    // Reset form to original values
    reset(toFormValues(signedInUser, organization));
    
    // Reset sync config to original values
    setSyncTeams(originalSyncTeams);
    setSyncRoles(originalSyncRoles);
    setAutoSyncLicensesEnabled(originalAutoSyncLicensesEnabled);
    
    // Reset lead status to original values
    setInvalidEmailLeadStatus(originalInvalidEmailLeadStatus);
    setValidEmailLeadStatus(originalValidEmailLeadStatus);
  };

  // Handler to open the create HubSpot property dialog
  const handleOpenCreatePropertyDialog = (
    target: 'emailStatus' | 'verificationDate' | 'emailSource' | 'jobChangeDate',
    fieldType: HubSpotFieldType,
    label: string
  ) => {
    setCreatePropertyTarget(target);
    setCreatePropertyFieldType(fieldType);
    setCreatePropertyLabel(label);
    setCreatePropertyDialogOpen(true);
  };

  // Handler to create a new HubSpot property
  const handleCreateHubSpotProperty = async (label: string, fieldType: HubSpotFieldType) => {
    setIsCreatingProperty(true);
    try {
      const { result, status, error } = await createHubSpotContactProperty({ label, fieldType });
      
      if (status === 200 && result) {
        // Add the new property to the list
        const newProperty: HubSpotContactProperty = {
          name: result.name,
          label: result.label,
          type: fieldType === 'select' ? 'enumeration' : fieldType === 'date' ? 'date' : 'string',
          fieldType: fieldType,
          hubspotDefined: false,
        };
        setHubspotProperties(prev => [...prev, newProperty].sort((a, b) => a.label.localeCompare(b.label)));
        
        // Set the form value for the target field using setValue
        if (createPropertyTarget === 'emailStatus') {
          setValue('organizationSettings.customField', result.name, { shouldDirty: true });
        } else if (createPropertyTarget === 'verificationDate') {
          setValue('organizationSettings.timestampCustomField', result.name, { shouldDirty: true });
        } else if (createPropertyTarget === 'emailSource') {
          setValue('organizationSettings.enrichedCustomField', result.name, { shouldDirty: true });
        } else if (createPropertyTarget === 'jobChangeDate') {
          setValue('organizationSettings.jobChangeDateCustomField', result.name, { shouldDirty: true });
        }
        
        toast.success(`Property "${result.label}" created successfully`);
        setCreatePropertyDialogOpen(false);
      } else {
        toast.error(error || "Failed to create property");
      }
    } catch (err: any) {
      console.error("Error creating HubSpot property:", err);
      toast.error(err.message || "Failed to create property");
    } finally {
      setIsCreatingProperty(false);
    }
  };

  const handleFormSubmit = async (data: AccountSettings) => {
    if (!signedInUser || !organization) return;
    setLoading(true);

    try {
      // For HubSpot, fields are strings (property names); for Outreach, they're numbers
      const isHubSpot = organization?.initialIntegrationType === "hubspot";
      
      const updatedSettings: AccountSettings = {
        ...data,
        organizationSettings: {
          ...data.organizationSettings,
          disableOptOutProspects:
            !data.organizationSettings.disableOptOutProspects,
          // Handle HubSpot (strings) vs Outreach (numbers) differently
          customField: isCustomFieldEnabled
            ? (isHubSpot 
                ? (customFieldValue || null) // HubSpot: keep as string
                : (Number(customFieldValue) || null)) // Outreach: convert to number
            : null,
          timestampCustomField: isTimestampCustomFieldEnabled
            ? (isHubSpot 
                ? (timestampCustomFieldValue || null) // HubSpot: keep as string
                : timestampCustomFieldNumberValue) // Outreach: convert to number
            : null,
          enrichedCustomField: isEnrichedCustomFieldEnabled
            ? (isHubSpot 
                ? (enrichedCustomFieldValue || null) // HubSpot: keep as string
                : enrichedCustomFieldNumberValue) // Outreach: convert to number
            : null,
          // Outreach only, so these stay numeric.
          linkedInUrnCustomField:
            !isHubSpot && isLinkedInUrnCustomFieldEnabled ? Number(linkedInUrnCustomFieldValue) || null : null,
          phoneStatusCustomField:
            !isHubSpot && isPhoneStatusCustomFieldEnabled ? Number(phoneStatusCustomFieldValue) || null : null,
          phoneVerifiedAtCustomField:
            !isHubSpot && isPhoneVerifiedAtCustomFieldEnabled ? Number(phoneVerifiedAtCustomFieldValue) || null : null,
          // HubSpot: a property name, or none (boring finds or creates "Changed roles on"); Outreach: a number.
          jobChangeDateCustomField: isJobChangeDateCustomFieldEnabled
            ? (isHubSpot ? (jobChangeDateCustomFieldValue || null) : (Number(jobChangeDateCustomFieldValue) || null))
            : null,
          // HubSpot: lead status to set for contacts with invalid emails
          invalidEmailLeadStatus: isHubSpot && data.organizationSettings.disableOptOutProspects
            ? (invalidEmailLeadStatus || null)
            : null,
          // HubSpot: lead status to set when email becomes valid
          validEmailLeadStatus: isHubSpot && data.organizationSettings.disableOptOutProspects
            ? (validEmailLeadStatus || null)
            : null,
          // Include sync config from state
          licenseSyncTeams: syncTeams,
          licenseSyncRoles: syncRoles,
          autoSyncLicensesEnabled: autoSyncLicensesEnabled,
          // Outreach: ownership change to whitelisted owner triggers verification
          ownerChangeTriggersVerification: data.organizationSettings.ownerChangeTriggersVerification ?? false,
          // HubSpot: unenroll/re-enroll on sequence when email invalid then enriched
          hubspotSequenceReenrollmentEnabled: data.organizationSettings.hubspotSequenceReenrollmentEnabled ?? false,
          // Outreach: verify and enrich when a prospect is opened; unset means on
          isAutoEnrichOnVisitEnabled: data.organizationSettings.isAutoEnrichOnVisitEnabled !== false,
          // LinkedIn activity on the CRM timeline; unset means on, backfill off
          isLinkedInActivityLoggingEnabled: data.organizationSettings.isLinkedInActivityLoggingEnabled !== false,
          isLinkedInActivityBackfillEnabled:
            data.organizationSettings.isLinkedInActivityLoggingEnabled !== false && data.organizationSettings.isLinkedInActivityBackfillEnabled === true,
          // A LinkedIn reply stops sequences and workflows; unset means on
          isStopSequencesOnLinkedInReplyEnabled: data.organizationSettings.isStopSequencesOnLinkedInReplyEnabled !== false,
          // HubSpot: whose contact it is (empty = Contact owner)
          ...(isHubSpot ? { ownerProperty: data.organizationSettings.ownerProperty || "" } : {}),
          // Outreach: boring's own actions in the activity feed; unset means on
          isBoringActivityLoggingEnabled: data.organizationSettings.isBoringActivityLoggingEnabled !== false,
          // availableLicenses is read-only, don't allow users to update it
        },
      };

      const convertedSettings =
        convertAppOrganizationSettingsToDbOrganizationSettings(
          updatedSettings.organizationSettings
        );

      updatedSettings.organizationSettings = convertedSettings;

      const { status: saveStatus } = await updateOrganizationSettings(
        removeUndefinedDeep<AccountSettings>(updatedSettings)
      );
      if (saveStatus === 403) {
        toast.error("Only organization admins can change these settings.");
        return;
      }
      if (saveStatus !== 200) {
        throw new Error(`Failed to update settings (Status: ${saveStatus})`);
      }

      const [organizationResponse, dbUser] = await Promise.all([
        getMyOrganization(),
        getDbUser(signedInUser.uid),
      ]);
      const updatedUser = convertDbAccountToAppAccount(
        dbUser,
        organizationResponse.result
      );
      const updatedOrg = convertDbOrganizationSettingsToAppOrganizationSettings(
        organizationResponse.result || {}
      );
      setOrganization(updatedOrg);
      setSignedInUser(updatedUser as AppUser);
      
      // Update sync config state from saved values
      const savedSyncTeams = updatedOrg.licenseSyncTeams || [];
      const savedSyncRoles = updatedOrg.licenseSyncRoles || [];
      const savedAutoSyncEnabled = updatedOrg.autoSyncLicensesEnabled || false;
      setSyncTeams(savedSyncTeams);
      setSyncRoles(savedSyncRoles);
      setAutoSyncLicensesEnabled(savedAutoSyncEnabled);
      setOriginalSyncTeams(savedSyncTeams);
      setOriginalSyncRoles(savedSyncRoles);
      setOriginalAutoSyncLicensesEnabled(savedAutoSyncEnabled);
      
      // Update lead status original values
      setOriginalInvalidEmailLeadStatus(invalidEmailLeadStatus);
      setOriginalValidEmailLeadStatus(validEmailLeadStatus);
      
      // Reset form dirty state after successful save
      reset(data, { keepValues: true });
      
      toast.success("Settings updated successfully");
    } catch (error) {
      console.error("Failed to update settings:", error?.toString());
      toast.error("Failed to update settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleIntegrationStatusUpdate = async () => {
    const isHubSpotIntegration = organization?.initialIntegrationType === "hubspot";
    
    try {
      setIsIntegrationUpdateLoading(true);
      
      if (isHubSpotIntegration) {
        // For HubSpot: toggle based on current authorization status
        if (isHubSpotAuthorized) {
          // Disconnect HubSpot
          const result = await disconnectHubSpot();
          
          if (result?.status === 200) {
            toast.success("HubSpot disconnected successfully");
            // Refresh user data
            const [organization, dbUser] = await Promise.all([
              getMyOrganization(),
              getDbUser(signedInUser?.uid || ""),
            ]);
            const updatedUser = convertDbAccountToAppAccount(
              dbUser,
              organization.result
            );
            setSignedInUser(updatedUser as AppUser);
            setOrganization(
              convertDbOrganizationSettingsToAppOrganizationSettings(
                organization.result || {}
              )
            );
          } else if (result?.status === 403) {
            toast.error("Only organization admins can disconnect HubSpot.");
          } else {
            toast.error("Failed to disconnect HubSpot");
          }
        } else {
          // Connect HubSpot - open auth URL
          openHubSpotAuthUrl();
        }
      } else {
        // For Outreach: toggle webhook status
        // If turning off, disconnect Outreach
        // Allow disconnect even if isIntegrationChecked is false (e.g. webhook status check failed due to expired tokens)
        if (isOutreachAuthorized) {
          const result = await disconnectOutreach();
          
          if (result?.status === 200) {
            toast.success("Outreach disconnected successfully");
            // Refresh user data
            const [organization, dbUser] = await Promise.all([
              getMyOrganization(),
              getDbUser(signedInUser?.uid || ""),
            ]);
            const updatedUser = convertDbAccountToAppAccount(
              dbUser,
              organization.result
            );
            setSignedInUser(updatedUser as AppUser);
            setOrganization(
              convertDbOrganizationSettingsToAppOrganizationSettings(
                organization.result || {}
              )
            );
            // Update webhook status
            const webhookStatus = await getOrgWebhookStatus();
            setIsIntegrationChecked(webhookStatus.result?.active);
          } else if (result?.status === 403) {
            toast.error("Only organization admins can disconnect Outreach.");
          } else {
            toast.error("Failed to disconnect Outreach");
          }
        } else {
          // If turning on, re-authenticate with Outreach to get fresh tokens and register webhook
          openOutreachAuthUrl();
        }
      }
      
      setIsIntegrationUpdateLoading(false);
    } catch (error) {
      console.error("Failed to update integration status:", error?.toString());
      toast.error("Failed to update integration status");
      setIsIntegrationUpdateLoading(false);
    }
  };


  useEffect(() => {
    const unsubscribe = subToEmailsInQueue(
      organization?.id || "",
      (emailsInQueue) => {
        setEmailsInQueue(emailsInQueue);
      }
    );

    return () => unsubscribe?.();
  }, [emailsInQueue, organization?.id]);

  // The Outreach access version connecting now would grant; an org below it (but past the
  // required version 2) is offered an optional upgrade, never prompted or disconnected.
  const [latestOutreachScopesVersion, setLatestOutreachScopesVersion] = useState<number | null>(null);
  const canUpgradeOutreach =
    !!organization?.isOutreachAuthorized && !organization?.outreachBaseScopesOnly && (organization?.outreachScopesVersion || 1) >= 2;
  useEffect(() => {
    if (!canUpgradeOutreach) return;
    let cancelled = false;
    getOutreachAuthUrl()
      .then((r) => {
        if (!cancelled && typeof r?.result?.scopesVersion === "number") setLatestOutreachScopesVersion(r.result.scopesVersion);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [canUpgradeOutreach, organization?.outreachScopesVersion]);

  useEffect(() => {
    const unsubscribe = subToRateLimit(
      organization?.id || "",
      (rateLimit) => {
        setRateLimit(rateLimit);
      }
    );

    return () => unsubscribe?.();
  }, [organization?.id]);

  useEffect(() => {
    reset(toFormValues(signedInUser, organization));
  }, [signedInUser, organization, reset]);

  useEffect(() => {
    if (!organization?.id) return;
    const updateWebhookStatus = async () => {
      try {
        const webhookStatus = await getOrgWebhookStatus();
        setIsIntegrationChecked(webhookStatus.result?.active ?? false);
      } catch (error) {
        console.error("Failed to get webhook status:", error?.toString());
        // Default to false so the toggle is still interactive (not stuck as undefined/disabled)
        // This allows users to disconnect even when webhook status check fails (e.g. expired tokens)
        setIsIntegrationChecked(false);
      }
    };

    updateWebhookStatus();
  }, [organization?.id]);

  // Fetch available teams and roles from Outreach, and users for license count
  useEffect(() => {
    const fetchTeamsAndRoles = async () => {
      if (!isOutreachAuthorized) return;
      
      setIsLoadingTeamsRoles(true);
      try {
        const { result, status } = await getOutreachUsers();
        if (status === 200 && result?.users) {
          const teams = new Set<string>();
          const roles = new Set<string>();
          
          // Parse included resources (profiles, teams)
          const includedResources = result.included || [];
          const profilesMap = new Map();
          const teamsMap = new Map();
          
          includedResources.forEach((resource: any) => {
            if (resource.type === "profile") {
              profilesMap.set(resource.id, resource);
            } else if (resource.type === "team") {
              teamsMap.set(resource.id, resource);
            }
          });
          
          // Transform users and extract teams/roles
          const whitelistedEmails = new Set(
            (organization?.allowedOwnerEmails || []).map((email) =>
              email.toLowerCase().trim()
            )
          );
          
          const transformedUsers = result.users.map((user: any) => {
            const email = user.attributes?.email || "";
            const isWhitelisted = whitelistedEmails.has(email.toLowerCase().trim());
            
            // Determine if user is active
            let isActive = true;
            if (user.attributes?.locked !== undefined) {
              isActive = !user.attributes.locked;
            } else if (user.attributes?.active !== undefined) {
              isActive = user.attributes.active;
            }
            
            return {
              email,
              isWhitelisted,
              isActive,
            };
          });
          
          setOutreachUsers(transformedUsers);
          
          // Extract teams and profiles from users
          result.users.forEach((user: any) => {
            // Extract profiles from relationships
            const profileId = user.relationships?.profile?.data?.id;
            if (profileId && profilesMap.has(profileId)) {
              const profileResource = profilesMap.get(profileId);
              const profileName = profileResource?.attributes?.name || profileResource?.attributes?.title;
              if (profileName) {
                roles.add(profileName);
              }
            }
            
            // Extract teams from relationships
            if (user.relationships?.teams?.data) {
              const teamRefs = Array.isArray(user.relationships.teams.data) 
                ? user.relationships.teams.data 
                : [user.relationships.teams.data];
              
              teamRefs.forEach((teamRef: any) => {
                if (teamRef?.id && teamsMap.has(teamRef.id)) {
                  const teamResource = teamsMap.get(teamRef.id);
                  const teamName = teamResource?.attributes?.name;
                  if (teamName) {
                    teams.add(teamName);
                  }
                }
              });
            }
          });
          
          const sortedTeams = Array.from(teams).sort();
          const sortedRoles = Array.from(roles).sort();
          
          devLog("📊 Extracted teams and profiles:", {
            teamsCount: sortedTeams.length,
            teams: sortedTeams,
            profilesCount: sortedRoles.length,
            profiles: sortedRoles,
            teamsMapSize: teamsMap.size,
            profilesMapSize: profilesMap.size,
          });
          
          setAvailableTeams(sortedTeams);
          setAvailableRoles(sortedRoles);
        }
      } catch (error) {
        console.error("Failed to fetch teams and roles:", error);
      } finally {
        setIsLoadingTeamsRoles(false);
      }
    };

    fetchTeamsAndRoles();
  }, [isOutreachAuthorized, organization?.allowedOwnerEmails]);

  // Fetch HubSpot users for license count AND teams/roles for sync config
  useEffect(() => {
    const fetchHubSpotUsers = async () => {
      if (!isHubSpotAuthorized || organization?.initialIntegrationType !== "hubspot") return;
      
      setIsLoadingLicenseUsers(true);
      setIsLoadingTeamsRoles(true);
      try {
        const { result, status } = await getHubSpotUsers();
        if (status === 200 && result?.users) {
          // Transform HubSpot users to match the same format as Outreach users
          const whitelistedEmails = new Set(
            (organization?.allowedOwnerEmails || []).map((email) =>
              email.toLowerCase().trim()
            )
          );
          
          // Extract unique teams and roles from HubSpot users
          const teamsSet = new Set<string>();
          const rolesSet = new Set<string>();
          
          const transformedUsers = result.users.map((user: any) => {
            const email = user.email || "";
            const isWhitelisted = whitelistedEmails.has(email.toLowerCase().trim());
            
            // Extract team names from HubSpot teams array
            const teamNames = user.teams?.map((team: { id: string; name: string; primary: boolean }) => team.name).filter(Boolean) || [];
            teamNames.forEach((team: string) => teamsSet.add(team));
            
            // Extract role from Settings Users API (merged by backend)
            const role = user.superAdmin ? "Super Admin" : (user.roleName || "");
            if (role) {
              rolesSet.add(role);
            }
            
            return {
              email,
              isWhitelisted,
              isActive: !user.archived,
              teams: teamNames,
              role,
            };
          });
          
          setOutreachUsers(transformedUsers); // Reuse the same state for license counting
          
          // Set available teams and roles for sync config dropdown
          const sortedTeams = Array.from(teamsSet).sort();
          const sortedRoles = Array.from(rolesSet).sort();
          
          devLog("📊 Extracted HubSpot teams and roles:", {
            teamsCount: sortedTeams.length,
            teams: sortedTeams.slice(0, 10), // Log first 10
            rolesCount: sortedRoles.length,
            roles: sortedRoles,
          });
          
          setAvailableTeams(sortedTeams);
          setAvailableRoles(sortedRoles);
        }
      } catch (error) {
        console.error("Failed to fetch HubSpot users:", error);
      } finally {
        setIsLoadingLicenseUsers(false);
        setIsLoadingTeamsRoles(false);
      }
    };

    fetchHubSpotUsers();
  }, [isHubSpotAuthorized, organization?.initialIntegrationType, organization?.allowedOwnerEmails]);

  // Fetch HubSpot contact properties for custom field dropdowns
  const fetchHubSpotProperties = async () => {
    if (!isHubSpotAuthorized || organization?.initialIntegrationType !== "hubspot") return;

    setIsLoadingHubspotProperties(true);
    try {
      const { result, status } = await getHubSpotContactProperties();
      if (status === 200 && result) {
        setHubspotProperties(result);
      } else if (status === 401) {
        devWarn("⚠️ Organization not authorized for HubSpot. Please re-authenticate.");
        setHubspotProperties([]);
      } else {
        console.error("Failed to fetch HubSpot properties:", { status, result });
        setHubspotProperties([]);
      }
    } catch (error) {
      console.error("Failed to fetch HubSpot properties:", error);
      setHubspotProperties([]);
    } finally {
      setIsLoadingHubspotProperties(false);
    }
  };

  useEffect(() => {
    fetchHubSpotProperties();
    // Refetch only when the connection changes; the refresh button covers the rest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHubSpotAuthorized, organization?.initialIntegrationType]);

  // Fetch Outreach custom fields for custom field dropdowns
  const fetchOutreachFields = async () => {
    // Check organization auth status (source of truth)
    const orgIsOutreachAuthed = organization?.isOutreachAuthorized === true || !!organization?.outreachAuthentication;
    const isAuthorized = isOutreachAuthorized || orgIsOutreachAuthed;

    if (!isAuthorized) {
      devLog("Skipping Outreach custom fields fetch - not authorized", {
        isOutreachAuthorized,
        orgIsOutreachAuthorized: organization?.isOutreachAuthorized,
        orgIsOutreachAuthed,
        initialIntegrationType: organization?.initialIntegrationType,
      });
      return;
    }

    devLog("Fetching Outreach custom fields", {
      isOutreachAuthorized,
      orgIsOutreachAuthorized: organization?.isOutreachAuthorized,
      orgIsOutreachAuthed,
      initialIntegrationType: organization?.initialIntegrationType,
    });

    setIsLoadingOutreachFields(true);
    try {
      const { result, status } = await getOutreachProspectCustomFields();
      if (status === 200 && result) {
        devLog("Successfully fetched Outreach custom fields", { count: result.length });
        setOutreachCustomFields(result);
      } else if (status === 401) {
        // Only warn if we thought we were authorized - token may have expired
        if (orgIsOutreachAuthed) {
          devWarn("⚠️ Outreach API returned 401 - token may have expired. Please re-authenticate.");
        } else {
          devLog("Outreach API returned 401 - organization not authorized");
        }
        setOutreachCustomFields([]);
      } else {
        console.error("Failed to fetch Outreach custom fields:", { status, result });
        setOutreachCustomFields([]);
      }
    } catch (error) {
      console.error("Failed to fetch Outreach custom fields:", error);
      setOutreachCustomFields([]);
    } finally {
      setIsLoadingOutreachFields(false);
    }
  };

  useEffect(() => {
    fetchOutreachFields();
    // Refetch only when the connection changes; the refresh button covers the rest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOutreachAuthorized, organization?.isOutreachAuthorized, organization?.initialIntegrationType]);

  // Fetch HubSpot lead statuses when authorized
  useEffect(() => {
    const fetchLeadStatuses = async () => {
      const isHubSpot = organization?.initialIntegrationType === "hubspot";
      const orgIsHubSpotAuthed = organization?.isHubSpotAuthorized === true;
      
      if (!isHubSpotAuthorized || !isHubSpot || !orgIsHubSpotAuthed) {
        return;
      }

      setIsLoadingLeadStatuses(true);
      try {
        const { result, status } = await getHubSpotLeadStatuses();
        if (status === 200 && result) {
          devLog("Successfully fetched HubSpot lead statuses", { count: result.length });
          setHubspotLeadStatuses(result);
        } else {
          console.error("Failed to fetch HubSpot lead statuses:", { status, result });
          setHubspotLeadStatuses([]);
        }
      } catch (error) {
        console.error("Failed to fetch HubSpot lead statuses:", error);
        setHubspotLeadStatuses([]);
      } finally {
        setIsLoadingLeadStatuses(false);
      }
    };

    fetchLeadStatuses();
  }, [isHubSpotAuthorized, organization?.isHubSpotAuthorized, organization?.initialIntegrationType]);

  // Update invalidEmailLeadStatus when organization changes
  useEffect(() => {
    const leadStatus = organization?.invalidEmailLeadStatus || "";
    setInvalidEmailLeadStatus(leadStatus);
    setOriginalInvalidEmailLeadStatus(leadStatus);
  }, [organization?.invalidEmailLeadStatus]);

  // Update validEmailLeadStatus when organization changes
  useEffect(() => {
    const leadStatus = organization?.validEmailLeadStatus || "";
    setValidEmailLeadStatus(leadStatus);
    setOriginalValidEmailLeadStatus(leadStatus);
  }, [organization?.validEmailLeadStatus]);

  // Initialize sync config from organization
  useEffect(() => {
    const orgSyncTeams = organization?.licenseSyncTeams || [];
    const orgSyncRoles = organization?.licenseSyncRoles || [];
    const orgAutoSyncEnabled = organization?.autoSyncLicensesEnabled || false;
    setSyncTeams(orgSyncTeams);
    setSyncRoles(orgSyncRoles);
    setAutoSyncLicensesEnabled(orgAutoSyncEnabled);
    setOriginalSyncTeams(orgSyncTeams);
    setOriginalSyncRoles(orgSyncRoles);
    setOriginalAutoSyncLicensesEnabled(orgAutoSyncEnabled);
  }, [organization?.licenseSyncTeams, organization?.licenseSyncRoles, organization?.autoSyncLicensesEnabled]);

  // Render Account section content
  // Only fields, so it sits on the page with no box.
  const renderAccountSection = () => (
    <FieldsSection>
      <SettingsHeader title="Account info" />
      <FieldGrid className="settings-field-grid">
        <Input label="Name" type="text" {...register("userSettings.firstName")} />
        <Input label="Title" type="text" {...register("userSettings.title")} />
        <Input label="Organization" type="text" disabled value={signedInUser?.organizationName} />
        <Input label="Email" type="text" disabled value={signedInUser?.email} />
        <Input label="Phone" type="tel" {...register("userSettings.phoneNumber")} />
        {/* HubSpot has no dark theme, so HubSpot orgs have nothing to choose. */}
        {!isHubSpotLook && (
        <Select
          label="Theme"
          fullWidth
          value={mode}
          onChange={(e: any) => {
            // MUI Select onChange: event.target.value contains the selected value
            const newMode = (e?.target?.value || e) as "light" | "dark";
            devLog("Theme dropdown changed. Event:", e, "New mode:", newMode);
            if (newMode === "light" || newMode === "dark") {
              setMode(newMode);
            }
          }}
          options={[
            { value: "light", label: "Light" },
            { value: "dark", label: "Dark" },
          ]}
        />
        )}
      </FieldGrid>
    </FieldsSection>
  );

  // Render Licenses section content
  const renderLicensesSection = () => {
    // Determine if license data is loading (HubSpot or Outreach users)
    const isHubSpotIntegration = organization?.initialIntegrationType === "hubspot";
    const isLicenseDataLoading = isHubSpotIntegration ? isLoadingLicenseUsers : isLoadingTeamsRoles;

    // Count ALL users with licenses (including inactive users)
    // Inactive users still count against license quota since their emails are verified
    const activeAssignedLicenses = outreachUsers.filter((user) => user.isWhitelisted).length;
    const totalLicenses = organization?.availableLicenses ?? null;
    const availableLicenses = totalLicenses !== null ? totalLicenses - activeAssignedLicenses : null;
    const licenseUtilization = totalLicenses !== null && totalLicenses > 0
      ? Math.round((activeAssignedLicenses / totalLicenses) * 100)
      : null;
    const licenseName = organization?.isEnrichmentEnabled ? "Enrich" : "Verify";
    const hasSyncTargets = syncTeams.length > 0 || syncRoles.length > 0;
    // A multiple Select may hand back a comma-joined string on autofill.
    const toList = (value: string | string[]) => (typeof value === "string" ? value.split(",") : value);
    // Keep saved teams and profiles pickable even when Outreach no longer lists them.
    const teamOptions = Array.from(new Set([...availableTeams, ...syncTeams])).map((team) => ({ value: team, label: team }));
    const roleOptions = Array.from(new Set([...availableRoles, ...syncRoles])).map((role) => ({ value: role, label: role }));

    return (
      <>
        <SettingsPanel>
          {totalLicenses !== null ? (
            <>
              <SettingsHeader
                title={licenseName}
                control={
                  <Button
                    variant={ButtonVariants.brand}
                    size={ButtonSizes.sm}
                    type="button"
                    onClick={() => {
                      // Self-serve (Stripe) orgs buy seats in-app; manually
                      // invoiced customers still go through us.
                      if (
                        organization?.billingType === "stripe" &&
                        organization?.billing?.subscriptionId
                      ) {
                        setIsAddLicensesOpen(true);
                        return;
                      }
                      if (organization?.billingType === "stripe") {
                        // No subscription yet — send them to checkout.
                        handleNavClick("billing");
                        return;
                      }
                                            openSupportChat();
                    }}
                  >
                    Add licenses
                  </Button>
                }
              />
              {isLicenseDataLoading ? (
                /* The count, its caption, the utilization line and its bar. */
                <PanelBody>
                  <div>
                    <BlockBone width={100} height={28} />
                    <TextBone width={200} style={{ marginTop: SPACINGS.xs }} />
                  </div>
                  <div>
                    <TextBone width={150} />
                    <BlockBone height={8} style={{ marginTop: SPACINGS.sm }} />
                  </div>
                </PanelBody>
              ) : (
                <PanelBody>
                  <div>
                    <LicenseCount>
                      {activeAssignedLicenses}/{totalLicenses}
                    </LicenseCount>
                    <SettingsCaption style={{ marginTop: SPACINGS.xs }}>
                      Assigned licenses, {availableLicenses !== null ? availableLicenses : 0} available
                    </SettingsCaption>
                  </div>
                  {licenseUtilization !== null && (
                    <div>
                      <SettingsCaption style={{ marginBottom: SPACINGS.sm }}>
                        {licenseUtilization}% license utilization
                      </SettingsCaption>
                      <LicenseUtilizationBar variant="determinate" value={licenseUtilization} />
                    </div>
                  )}
                </PanelBody>
              )}
            </>
          ) : (
            <SettingsHeader title="Licenses" caption="Licenses not set" />
          )}
        </SettingsPanel>

        <SettingsPanel>
          <SettingsHeader
            title="License sync configuration"
            caption="Configure which teams and profiles should automatically receive boring licenses when syncing. Users from these teams/profiles will get licenses, and licenses will be removed from users who have moved to other teams or became inactive."
          />
          <TwoFieldGrid>
            {isLoadingTeamsRoles ? (
              <>
                <BlockBone height={36} />
                <BlockBone height={36} />
              </>
            ) : (
              <>
                <Select
                  multiple
                  fullWidth
                  label="Teams"
                  value={syncTeams}
                  options={teamOptions}
                  renderValue={(value) => toList(value).join(", ")}
                  onChange={(e) => setSyncTeams(toList(e.target.value))}
                />
                <Select
                  multiple
                  fullWidth
                  label="Profiles"
                  value={syncRoles}
                  options={roleOptions}
                  renderValue={(value) => toList(value).join(", ")}
                  onChange={(e) => setSyncRoles(toList(e.target.value))}
                />
              </>
            )}
          </TwoFieldGrid>
          <SettingsRow
            label="Automatically trigger license syncing daily"
            caption={hasSyncTargets ? undefined : "Pick at least one team or profile first."}
            control={
              <Switch
                checked={autoSyncLicensesEnabled}
                onChange={(e) => setAutoSyncLicensesEnabled(e.target.checked)}
                disabled={!hasSyncTargets}
              />
            }
          />
        </SettingsPanel>

        <ProductLicenses teams={availableTeams} roles={availableRoles} />
      </>
    );
  };

  // Render Configuration section: the workflow switches
  const renderWorkflowsPanel = () => (
    <SettingsPanel>
      <SettingsHeader
        title="Workflows"
        caption={`What boring does on its own when it checks a ${isHubSpot ? "contact" : "prospect"}.`}
      />
      <SettingsGroup>
        {isHubSpot ? (
          // HubSpot: Set lead status for contacts with invalid emails
          <>
            <SettingsRow
              label="Set lead status for contacts with invalid emails"
              caption="When an email is detected as invalid, the contact's lead status is updated to the value you pick."
              control={
                <Controller
                  control={control}
                  name="organizationSettings.disableOptOutProspects"
                  render={({ field: { onChange, value } }) => (
                    <Switch
                      checked={value}
                      onChange={(e) => {
                        onChange(e.target.checked);
                        // Clear lead statuses if disabled
                        if (!e.target.checked) {
                          setInvalidEmailLeadStatus("");
                          setValidEmailLeadStatus("");
                        }
                      }}
                    />
                  )}
                />
              }
            />
            {watch("organizationSettings.disableOptOutProspects") && (
              <>
                <SettingsRow
                  label="Lead status when the email is invalid"
                  control={
                    <SettingsControlField>
                      {isLoadingLeadStatuses ? (
                        <BlockBone height={36} />
                      ) : (
                        <Select
                          fullWidth
                          displayEmpty
                          value={invalidEmailLeadStatus}
                          onChange={(e) => setInvalidEmailLeadStatus(e.target.value as string)}
                          options={[
                            { value: "", label: "Select a lead status" },
                            ...hubspotLeadStatuses.map(status => ({
                              value: status.value,
                              label: status.label,
                            })),
                          ]}
                        />
                      )}
                    </SettingsControlField>
                  }
                />
                <SettingsRow
                  label="Lead status when the email becomes valid"
                  control={
                    <SettingsControlField>
                      {isLoadingLeadStatuses ? (
                        <BlockBone height={36} />
                      ) : (
                        <Select
                          fullWidth
                          displayEmpty
                          value={validEmailLeadStatus}
                          onChange={(e) => setValidEmailLeadStatus(e.target.value as string)}
                          options={[
                            { value: "", label: "Clear lead status (blank)" },
                            ...hubspotLeadStatuses.map(status => ({
                              value: status.value,
                              label: status.label,
                            })),
                          ]}
                        />
                      )}
                    </SettingsControlField>
                  }
                />
              </>
            )}
            <SettingsRow
              label="Re-enroll contacts in their sequence after enrichment"
              caption="When a contact has an invalid email in a sequence, re-enroll them after we enrich a new email. Requires a HubSpot workflow that unenrolls on invalid email; we capture their sequence info and re-enroll after enrichment."
              control={
                <Controller
                  control={control}
                  name="organizationSettings.hubspotSequenceReenrollmentEnabled"
                  render={({ field: { onChange, value } }) => (
                    <Switch checked={value ?? false} onChange={(e) => onChange(e.target.checked)} />
                  )}
                />
              }
            />
          </>
        ) : (
          // Outreach: Opt out prospects with invalid emails
          <>
            <SettingsRow
              label="Opt prospects out of email when their email is invalid"
              caption="An email to an invalid address bounces, and a bounce stops the whole sequence. Opting the prospect out of email lets the sequence carry on with its call and email tasks."
              control={
                <Controller
                  control={control}
                  name="organizationSettings.disableOptOutProspects"
                  render={({ field: { onChange, value } }) => (
                    <Switch checked={value} onChange={(e) => onChange(e.target.checked)} />
                  )}
                />
              }
            />
            <SettingsRow
              label="Verify when ownership changes to a whitelisted owner"
              caption="Useful when prospects are reassigned to reps: the new owner's prospects are verified."
              control={
                <Controller
                  control={control}
                  name="organizationSettings.ownerChangeTriggersVerification"
                  render={({ field: { onChange, value } }) => (
                    <Switch checked={value ?? false} onChange={(e) => onChange(e.target.checked)} />
                  )}
                />
              }
            />
            <SettingsRow
              label="Verify and enrich on every profile visit"
              caption="When a rep opens a prospect in Outreach, boring checks the email and phone numbers on its own, at most once an hour per prospect, using cached results so it costs next to nothing. Turn off to run only when someone clicks Enrich."
              control={
                <Controller
                  control={control}
                  name="organizationSettings.isAutoEnrichOnVisitEnabled"
                  render={({ field: { onChange, value } }) => (
                    <Switch checked={value !== false} onChange={(e) => onChange(e.target.checked)} />
                  )}
                />
              }
            />
          </>
        )}
        <SettingsRow
          label={`Log LinkedIn activity to ${isHubSpot ? HUBSPOT_SYSTEM.name : OUTREACH_SYSTEM.name}`}
          caption={
            isHubSpot
              ? "LinkedIn messages sent and received, connection requests and acceptances, and comments and reactions on a contact's posts appear on the contact's timeline as LinkedIn messages, owned by the rep and linked to their company. Each is logged once, whoever on the team sees it."
              : organization?.outreachS2SInstallId
              ? "LinkedIn messages sent and received, connection requests and acceptances, and comments and reactions on a prospect's posts appear in the prospect's activity feed as boring events. Each is logged once, whoever on the team sees it."
              : "LinkedIn messages sent and received, connection requests and acceptances, and comments and reactions on a prospect's posts appear in the prospect's activity feed as completed tasks, owned by the rep. Install boring's Outreach app to show them as boring events instead. Each is logged once, whoever on the team sees it."
          }
          control={
            <Controller
              control={control}
              name="organizationSettings.isLinkedInActivityLoggingEnabled"
              render={({ field: { onChange, value } }) => <Switch checked={value !== false} onChange={(e) => onChange(e.target.checked)} />}
            />
          }
        />
        {orgPlanLocks(organization, "linkedinActivity") && <PlanUpgradePrompt feature="linkedinActivity" what="the LinkedIn activity feed" />}
        <SettingsRow
          label="Include earlier LinkedIn messages"
          caption={`The first time boring sees a conversation, also log its messages from the last 30 days (up to 50), not only new ones.`}
          control={
            <Controller
              control={control}
              name="organizationSettings.isLinkedInActivityBackfillEnabled"
              render={({ field: { onChange, value } }) => (
                <Switch checked={value === true && isLinkedInActivityLoggingEnabled !== false} disabled={isLinkedInActivityLoggingEnabled === false} onChange={(e) => onChange(e.target.checked)} />
              )}
            />
          }
        />
        <SettingsRow
          label="Stop sequences when someone replies on LinkedIn"
          caption={
            isHubSpot
              ? "A LinkedIn reply from a contact takes them out of every workflow they are in, and sets the contact's \"boring LinkedIn replied\" date. HubSpot does not let apps unenroll from sequences, so add a workflow on that property with the \"Unenroll from sequence\" action (Sales Hub Enterprise)."
              : "A LinkedIn reply from a prospect finishes every sequence they are in, whoever's it is, as an email reply would. Sequences they were added to after replying keep running."
          }
          control={
            <Controller
              control={control}
              name="organizationSettings.isStopSequencesOnLinkedInReplyEnabled"
              render={({ field: { onChange, value } }) => <Switch checked={value !== false} onChange={(e) => onChange(e.target.checked)} />}
            />
          }
        />
        {isHubSpot && (
          <SettingsRow
            label="Owner field"
            caption="Whose contact it is: boring processes the contacts of your licensed reps by this field, and uses it for the LinkedIn sender in workflows and sequences and for job change owners. Contact owner unless your team owns contacts by another field (for example MDA Owner)."
            control={
              <Controller
                control={control}
                name="organizationSettings.ownerProperty"
                render={({ field: { onChange, value } }) => {
                  const current = String(value || "");
                  const options: SearchableSelectOption[] = [
                    { value: "", label: "Contact owner", secondary: "hubspot_owner_id", pinned: true },
                    ...hubspotProperties
                      .filter((p) => p.name !== "hubspot_owner_id" && p.type === "enumeration" && /owner/i.test(`${p.name} ${p.label}`))
                      .map((p) => ({ value: p.name, label: p.label || p.name, secondary: p.name })),
                  ];
                  if (current && !options.some((o) => o.value === current)) options.push({ value: current, label: current });
                  return <SearchableSelect fullWidth placeholder="Contact owner" value={current} onChange={(v) => onChange(v || "")} options={options} />;
                }}
              />
            }
          />
        )}
        {!isHubSpot && (
          <SettingsRow
            label={`Log boring's activity to ${OUTREACH_SYSTEM.name}`}
            caption={
              organization?.outreachS2SInstallId
                ? "What boring does to a prospect appears in its activity feed as boring events: each verification result, a new email address or phone number boring found, profile updates from LinkedIn, job changes detected and applied, and opt-outs over an invalid email and their reversal. Each is posted once."
                : "What boring does to a prospect (verification results, new email addresses and phone numbers, profile updates, job changes, opt-outs) appears in its activity feed as boring events once an admin installs boring's Outreach app. Nothing is posted until then."
            }
            control={
              <Controller
                control={control}
                name="organizationSettings.isBoringActivityLoggingEnabled"
                render={({ field: { onChange, value } }) => <Switch checked={value !== false} onChange={(e) => onChange(e.target.checked)} />}
              />
            }
          />
        )}
      </SettingsGroup>
    </SettingsPanel>
  );

  // Render Configuration section: the field mapping table, on the page
  // with no box, like the other sections that are only fields
  const renderFieldMapping = () => {
    const recordNoun = isHubSpot ? "contact" : "prospect";
    const destination = isHubSpot ? HUBSPOT_SYSTEM : OUTREACH_SYSTEM;
    const isLoadingFields = isHubSpot ? isLoadingHubspotProperties : isLoadingOutreachFields;
    // What each mapping holds right now, for keeping the email mappings apart.
    const mappingValues: Record<MappingFieldName, unknown> = {
      "organizationSettings.customField": customFieldValue,
      "organizationSettings.timestampCustomField": timestampCustomFieldValue,
      "organizationSettings.enrichedCustomField": enrichedCustomFieldValue,
      "organizationSettings.linkedInUrnCustomField": linkedInUrnCustomFieldValue,
      "organizationSettings.phoneStatusCustomField": phoneStatusCustomFieldValue,
      "organizationSettings.phoneVerifiedAtCustomField": phoneVerifiedAtCustomFieldValue,
      "organizationSettings.jobChangeDateCustomField": jobChangeDateCustomFieldValue,
    };
    const mappings: FieldMappingConfig[] = [
      {
        id: "email-status",
        label: "Email status",
        detail: "Valid · Invalid · Questionable",
        hint: `Populate a ${recordNoun} custom field with their email status. This will be Valid, Invalid, or Questionable.`,
        switchName: "organizationSettings.isCustomFieldEnabled",
        fieldName: "organizationSettings.customField",
        excludes: ["organizationSettings.timestampCustomField", "organizationSettings.enrichedCustomField"],
        hubSpot: { target: "emailStatus", fieldType: "select", propertyLabel: "Email status", createFirst: true },
      },
      {
        id: "verification-date",
        label: "Email verification date",
        detail: "Date and time",
        hint: `Populate a ${recordNoun} custom field with the date and time that their email was last verified.`,
        switchName: "organizationSettings.isTimestampCustomFieldEnabled",
        fieldName: "organizationSettings.timestampCustomField",
        excludes: ["organizationSettings.customField", "organizationSettings.enrichedCustomField"],
        hubSpot: { target: "verificationDate", fieldType: "date", propertyLabel: "Email verification date", createFirst: true },
      },
      ...(organization?.isEnrichmentEnabled
        ? [
            {
              id: "email-source",
              label: "Email source",
              detail: "“Enriched by boring”",
              hint: `Populate a ${recordNoun} custom field with the words "Enriched by boring" any time boring enriches your ${recordNoun}'s email address.`,
              switchName: "organizationSettings.isEnrichedCustomFieldEnabled",
              fieldName: "organizationSettings.enrichedCustomField",
              excludes: ["organizationSettings.customField", "organizationSettings.timestampCustomField"],
              hubSpot: { target: "emailSource", fieldType: "text", propertyLabel: "Email source", createFirst: false },
            } as FieldMappingConfig,
          ]
        : []),
      {
        id: "job-change-date",
        label: "Changed roles on",
        detail: "Date",
        hint: `Stamp a ${recordNoun} custom field with the day boring detected they changed jobs, so workflows and lists can act on it.`,
        switchName: "organizationSettings.isJobChangeDateCustomFieldEnabled",
        fieldName: "organizationSettings.jobChangeDateCustomField",
        hubSpot: { target: "jobChangeDate", fieldType: "date", propertyLabel: "Changed roles on", createFirst: true },
      },
      {
        id: "linkedin-id",
        label: "LinkedIn ID",
        detail: "LinkedIn member id",
        hint: "Write the LinkedIn member id behind the prospect's profile to a custom field, so the person stays identifiable when their profile URL changes.",
        switchName: "organizationSettings.isLinkedInUrnCustomFieldEnabled",
        fieldName: "organizationSettings.linkedInUrnCustomField",
      },
      {
        id: "phone-status",
        label: "Phone status",
        detail: "Valid · Invalid · Questionable · Unverified",
        hint: "Each enrich checks the prospect's phone numbers and writes the primary number's result (Valid, Invalid, Questionable or Unverified) to a custom field.",
        switchName: "organizationSettings.isPhoneStatusCustomFieldEnabled",
        fieldName: "organizationSettings.phoneStatusCustomField",
      },
      {
        id: "phone-verification-date",
        label: "Phone verification date",
        detail: "Date and time",
        hint: "Write the date and time of each phone check to a custom field, so you can tell how fresh a phone status is.",
        switchName: "organizationSettings.isPhoneVerifiedAtCustomFieldEnabled",
        fieldName: "organizationSettings.phoneVerifiedAtCustomField",
      },
    ];
    // HubSpot writes only the email mappings; the rest address Outreach's customN.
    const visibleMappings = isHubSpot ? mappings.filter((mapping) => mapping.hubSpot) : mappings;

    // Outreach: a numbered custom field, stored as a number.
    const renderOutreachFieldSelect = (mapping: FieldMappingConfig, syncing: boolean) => (
      <Controller
        control={control}
        name={mapping.fieldName}
        render={({ field: { onChange, value } }) => {
          const stringValue = value != null ? value.toString() : "";
          // Leave out fields a sibling mapping writes to, but keep this one's own.
          const taken = (mapping.excludes || []).map((name) => mappingValues[name]?.toString() || "");
          const availableFields = outreachCustomFields.filter((field) => {
            const fieldStr = field.fieldNumber.toString();
            return fieldStr === stringValue || !taken.includes(fieldStr);
          });
          return (
            <SearchableSelect
              fullWidth
              placeholder="Search custom fields"
              value={stringValue || undefined}
              onChange={(raw) => onChange(raw ? parseInt(raw, 10) : null)}
              disabled={!syncing}
              options={[
                ...(stringValue && !availableFields.some((field) => field.fieldNumber.toString() === stringValue)
                  ? [{ value: stringValue, label: `custom${stringValue}` }]
                  : []),
                ...availableFields.map((field) => ({
                  value: field.fieldNumber.toString(),
                  label: field.label || field.name,
                  secondary: field.name,
                })),
              ]}
            />
          );
        }}
      />
    );

    // HubSpot: a contact property, stored by name, with a way to create one.
    // Portals carry hundreds of properties, so it is type-to-search by label or internal name.
    const renderHubSpotPropertySelect = (
      mapping: FieldMappingConfig,
      hubSpot: NonNullable<FieldMappingConfig["hubSpot"]>,
      syncing: boolean
    ) => (
      <Controller
        control={control}
        name={mapping.fieldName}
        render={({ field: { onChange, value } }) => {
          const current = value != null ? value.toString() : "";
          const createOption: SearchableSelectOption = { value: "__create__", label: "+ Create new property", pinned: true };
          const propertyOptions: SearchableSelectOption[] = hubspotProperties.map((prop) => ({
            value: prop.name,
            label: prop.label || prop.name,
            secondary: prop.name,
          }));
          // A property boring writes to but the list lacks (deleted, or not fetched) still shows.
          if (current && current !== "__create__" && !hubspotProperties.some((p) => p.name === current)) {
            propertyOptions.unshift({ value: current, label: current });
          }
          return (
            <SearchableSelect
              fullWidth
              placeholder="Search properties"
              value={current && current !== "__create__" ? current : undefined}
              onChange={(selectedValue) => {
                if (selectedValue === "__create__") {
                  handleOpenCreatePropertyDialog(hubSpot.target, hubSpot.fieldType, hubSpot.propertyLabel);
                  return;
                }
                onChange(selectedValue ? selectedValue : null);
              }}
              disabled={!syncing}
              options={hubSpot.createFirst ? [createOption, ...propertyOptions] : [...propertyOptions, createOption]}
            />
          );
        }}
      />
    );

    return (
      <FieldMappingTable
        title="Field mapping"
        description={
          isHubSpot
            ? "Choose which HubSpot contact properties boring writes to."
            : "Choose which Outreach custom fields boring writes to."
        }
        source={BORING_SYSTEM}
        destination={destination}
        onRefresh={isHubSpot ? fetchHubSpotProperties : fetchOutreachFields}
        refreshing={isLoadingFields}
      >
        {visibleMappings.map((mapping) => (
          <Controller
            key={mapping.id}
            control={control}
            name={mapping.switchName}
            render={({ field: { onChange, value } }) => {
              const syncing = value === true;
              let fieldSelect: ReactNode;
              if (isLoadingFields) {
                fieldSelect = <BlockBone height={36} />;
              } else if (isHubSpot && mapping.hubSpot) {
                fieldSelect = renderHubSpotPropertySelect(mapping, mapping.hubSpot, syncing);
              } else {
                fieldSelect = renderOutreachFieldSelect(mapping, syncing);
              }
              return (
                <FieldMappingRow
                  id={mapping.id}
                  label={mapping.label}
                  detail={mapping.detail}
                  hint={mapping.hint}
                  syncing={syncing}
                  onSyncingChange={onChange}
                  destinationName={destination.name}
                >
                  {fieldSelect}
                </FieldMappingRow>
              );
            }}
          />
        ))}
      </FieldMappingTable>
    );
  };

  // Render Integration section content
  const renderIntegrationSection = () => {
    const isOutreachIntegration = !organization?.initialIntegrationType || organization?.initialIntegrationType === "outreach";
    // Connected = OAuth done (org source of truth). Don't require webhook for "Connected".
    const isConnected = isOutreachIntegration
      ? !!isOutreachAuthorized
      : !!isHubSpotAuthorized;
    const needsOutreachReconnect =
      organization?.isOutreachAuthorized &&
      !organization?.outreachBaseScopesOnly &&
      (organization?.outreachScopesVersion || 1) < 2;
    // Everything works on version 2; later versions add what AI apps (the boring MCP) can read
    // and change in Outreach. Optional, so it is offered, not warned about.
    const offerOutreachUpgrade =
      canUpgradeOutreach && latestOutreachScopesVersion !== null && (organization?.outreachScopesVersion || 1) < latestOutreachScopesVersion;

    let integrationControl: ReactNode;
    if (isOutreachIntegration) {
      integrationControl = isConnected ? (
        <Switch
          checked={!!isIntegrationChecked}
          onChange={() => handleIntegrationStatusUpdate()}
          disabled={isIntegrationUpdateLoading}
        />
      ) : (
        <Button
          variant={ButtonVariants.brand}
          type="button"
          onClick={openOutreachAuthUrl}
          disabled={isAuthUrlLoading || isIntegrationUpdateLoading}
        >
          {isAuthUrlLoading ? "Connecting..." : "Connect"}
        </Button>
      );
    } else {
      integrationControl = isHubSpotAuthorized ? (
        <Switch
          checked={isHubSpotAuthorized}
          onChange={() => handleIntegrationStatusUpdate()}
          disabled={isIntegrationUpdateLoading}
        />
      ) : (
        <Button
          variant={ButtonVariants.brand}
          type="button"
          onClick={openHubSpotAuthUrl}
          disabled={isHubSpotAuthUrlLoading || isIntegrationUpdateLoading}
        >
          {isHubSpotAuthUrlLoading ? "Connecting..." : "Connect"}
        </Button>
      );
    }

    return (
      <>
        <SettingsPanel>
          <SettingsHeader title="Status" />
          <SettingsGroup>
            {offerOutreachUpgrade && (
              <SettingsRow
                label="Upgrade Outreach access (optional)"
                caption="Everything in boring works as it is. Reconnecting grants boring the newest Outreach access, so AI apps connected to boring (the boring MCP) can read and change more of Outreach: opportunities, sent emails, notes, snippets, bulk updates and the rest."
                control={
                  <Button
                    variant={ButtonVariants.light}
                    type="button"
                    onClick={async () => {
                      const r = await getOutreachAuthUrl();
                      const url = r?.result?.outreachAuthUrl;
                      if (url) window.location.href = url;
                    }}
                  >
                    Upgrade Outreach access
                  </Button>
                }
              />
            )}
            {needsOutreachReconnect && (
              <SettingsRow
                label="Reconnect Outreach"
                caption="Outreach needs a quick reconnect to enable sequence enrollment on export and dialer dispositions from Outreach."
                control={
                  <Button
                    variant={ButtonVariants.brand}
                    type="button"
                    onClick={async () => {
                      const r = await getOutreachAuthUrl();
                      const url = r?.result?.outreachAuthUrl;
                      if (url) window.location.href = url;
                    }}
                  >
                    Reconnect Outreach
                  </Button>
                }
              />
            )}
            <SettingsRow
              label={
                <IntegrationName>
                  {isOutreachIntegration ? <OutreachMark /> : <HubSpotMark />}
                  {isOutreachIntegration ? "Outreach" : "HubSpot"}
                </IntegrationName>
              }
              caption={
                isConnected ? (
                  <IntegrationStatus>
                    <ShapeColor
                      color={
                        isIntegrationUpdateLoading
                          ? COLORS.integrationLoading
                          : COLORS.integrationConnected
                      }
                    >
                      <CircleSvg />
                    </ShapeColor>
                    {isIntegrationUpdateLoading ? "Loading" : "Connected"}
                  </IntegrationStatus>
                ) : (
                  "Not connected"
                )
              }
              control={integrationControl}
            />
          </SettingsGroup>
        </SettingsPanel>

        {/* The extension's connection beside the CRM's, then what boring is doing right now:
            a HubSpot report card, or Outreach's report layout with its rate limit. */}
        <SettingsPanel>
          <SettingsHeader title="Chrome extension" />
          <ExtensionSection />
        </SettingsPanel>

        {hubspotSection ? (
          <LiveVerificationsCard inQueue={emailsInQueue} />
        ) : (
          <LiveVerificationsPanel inQueue={emailsInQueue} rateLimit={isOutreachIntegration ? rateLimit : null} />
        )}
      </>
    );
  };

  const scrollableContentRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);

  const handleNavClick = (section: SettingsSection) => {
    setActiveSection(section);
    const element = document.getElementById(`section-${section}`);
    const scrollContainer = scrollableContentRef.current;
    
    if (element && scrollContainer) {
      // Scroll to section with padding to account for sticky header
      // Header height is approximately: top padding (1.5rem) + title (~1.5rem) + description margin + description (~0.875rem) + bottom padding (1.5rem) ≈ 100-120px
      const elementTop = element.offsetTop;
      const headerOffset = 120; // Enough padding to clear the sticky header
      const targetScroll = Math.max(0, elementTop - headerOffset);
      
      // Mark that we're programmatically scrolling to prevent scroll handler from overriding
      isScrollingRef.current = true;
      
      // Use requestAnimationFrame to ensure smooth scroll
      requestAnimationFrame(() => {
        if (scrollContainer) {
          scrollContainer.scrollTop = targetScroll;
          // Reset the flag after a short delay to allow scroll to complete
          setTimeout(() => {
            isScrollingRef.current = false;
          }, 100);
        }
      });
    }
  };

  // Opened from an upgrade link (?open=billing): go to Billing once the sections have laid out.
  useEffect(() => {
    if (hubspotSection || initialSection !== "billing") return;
    const timer = setTimeout(() => handleNavClick("billing"), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const scrollContainer = scrollableContentRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      // Don't update active section if we're programmatically scrolling
      if (isScrollingRef.current) return;

      const sections = navigationItems.map((item) => item.value);
      const scrollTop = scrollContainer.scrollTop;
      const headerOffset = 120; // Match the offset used in handleNavClick

      // Find which section is currently at the top
      let activeSectionFound: SettingsSection | null = null;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const sectionId = sections[i];
        const element = document.getElementById(`section-${sectionId}`);
        if (element) {
          const elementTop = element.offsetTop;
          // Check if we've scrolled past this section's top (accounting for header offset)
          // We add the header offset to scrollTop to account for the visual position
          if (scrollTop + headerOffset >= elementTop) {
            activeSectionFound = sectionId;
            break;
          }
        }
      }
      
      // If no section found, default to the first one
      if (activeSectionFound) {
        setActiveSection(activeSectionFound);
      } else if (sections.length > 0) {
        setActiveSection(sections[0]);
      }
    };

    handleScroll();
    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, []);

  if (hubspotSection) {
    const pageDef = HUBSPOT_PAGES[hubspotSection] ?? HUBSPOT_PAGES.account;
    const tab = hubspotTab[hubspotSection] ?? pageDef.tabs?.[0]?.value ?? "";
    const inForm = hubspotSection !== "linkedin" && hubspotSection !== "export" && hubspotSection !== "ai-apps";
    const body = (() => {
      switch (hubspotSection) {
        case "licenses":
          return renderLicensesSection();
        case "billing":
          return tab === "usage" ? <MeteredUsageSection /> : <BillingSection />;
        case "addons":
          return tab === "phone" ? <PhoneEnrichmentSection /> : <AddonsSection />;
        case "export":
          // Admins set it for the team; reps see the same settings read-only.
          return tab === "history" ? <ExportHistorySection /> : <ExportSection />;
        case "integration-status":
          return renderIntegrationSection();
        case "workflows":
          return tab === "mapping" ? renderFieldMapping() : tab === "jobChanges" ? <JobChangeSettings /> : renderWorkflowsPanel();
        case "dialer":
          return tab === "team" ? <DialerSection /> : <MyDialerSection />;
        case "linkedin":
          return <LinkedInAutomationTab />;
        case "ai-apps":
          return <ConnectedAiApps />;
        default:
          return renderAccountSection();
      }
    })();
    return (
      <>
        <HubSpotSettingsPage
          title={hubSpotSettingsLabel(hubspotSection)}
          tabs={pageDef.tabs}
          tab={tab}
          onTab={(value) => setHubspotTab((prev) => ({ ...prev, [hubspotSection]: value }))}
          description={pageDef.description}
        >
          <div className={`hs-settings-body hs-settings-body--${hubspotSection}`}>
            {inForm ? (
              <form onSubmit={handleSubmit(onSubmit)} id="settings-form">
                {body}
              </form>
            ) : (
              body
            )}
          </div>
        </HubSpotSettingsPage>
        {hasUnsavedChanges && (
          <div className="hs-savebar" role="region" aria-label="Unsaved changes">
            <HsPrimaryButton type="submit" form="settings-form" loading={loading} onClick={handleSubmit(onSubmit)}>
              Save
            </HsPrimaryButton>
            <HsSecondaryButton onClick={handleCancel} disabled={loading}>
              Cancel
            </HsSecondaryButton>
            <span className="hs-savebar__note">You have unsaved changes</span>
          </div>
        )}
        <AddLicensesDialog open={isAddLicensesOpen} onClose={() => setIsAddLicensesOpen(false)} />
        <CreateHubSpotPropertyDialog
          open={createPropertyDialogOpen}
          onClose={() => setCreatePropertyDialogOpen(false)}
          onCreateProperty={handleCreateHubSpotProperty}
          fieldType={createPropertyFieldType}
          fieldLabel={createPropertyLabel}
          isCreating={isCreatingProperty}
        />
      </>
    );
  }

  return (
    <>
      <SettingsCard className="settings-card" data-theme={mode}>
        <SettingsHeaderBar className="settings-header">
          <PageTitle>Settings</PageTitle>
          <SettingsCaption style={{ marginTop: SPACINGS.xs }}>
            Manage your account settings, licenses, and integrations
          </SettingsCaption>
        </SettingsHeaderBar>
        <SettingsContainer>
          <Sidebar>
            {navigationItems.map((item) => (
              <SidebarNavItem
                key={item.value}
                active={activeSection === item.value}
                onClick={() => handleNavClick(item.value)}
                type="button"
              >
                {item.label}
              </SidebarNavItem>
            ))}
          </Sidebar>
          <ContentArea ref={scrollableContentRef} className="settings-content-area">
            <form onSubmit={handleSubmit(onSubmit)} id="settings-form">
              <SectionWrapper id="section-account">
                <ContentHeader variant={TextVariant.lg}>Account</ContentHeader>
                {renderAccountSection()}
              </SectionWrapper>
              <SectionWrapper id="section-licenses">
                <ContentHeader variant={TextVariant.lg}>Licenses</ContentHeader>
                {renderLicensesSection()}
              </SectionWrapper>
              <SectionWrapper id="section-billing">
                <ContentHeader variant={TextVariant.lg}>Billing</ContentHeader>
                <BillingSection />
                <MeteredUsageSection />
              </SectionWrapper>
              <SectionWrapper id="section-addons">
                <ContentHeader variant={TextVariant.lg}>Add-ons</ContentHeader>
                <AddonsSection />
                <PhoneEnrichmentSection />
                <ExportSection />
              </SectionWrapper>
              <SectionWrapper id="section-workflows">
                <ContentHeader variant={TextVariant.lg}>Configuration</ContentHeader>
                {renderWorkflowsPanel()}
                {renderFieldMapping()}
                <JobChangeSettings />
              </SectionWrapper>
              <SectionWrapper id="section-integration-status">
                <ContentHeader variant={TextVariant.lg}>Integration</ContentHeader>
                {renderIntegrationSection()}
              </SectionWrapper>
              <SectionWrapper id="section-dialer">
                <ContentHeader variant={TextVariant.lg}>Dialer</ContentHeader>
                <MyDialerSection />
                <DialerSection />
              </SectionWrapper>
            </form>
            {/* The same settings as the dashboard's LinkedIn tab, saved on their
                own. Outside the form, so Enter in a pacing field never submits
                the rest of Settings. */}
            <SectionWrapper id="section-linkedin">
              <ContentHeader variant={TextVariant.lg}>LinkedIn</ContentHeader>
              <LinkedInAutomationTab />
            </SectionWrapper>
            {/* Outside the form too: Enter in its search box must not submit Settings. */}
            <SectionWrapper id="section-history">
              <ContentHeader variant={TextVariant.lg}>History</ContentHeader>
              <ExportHistorySection />
            </SectionWrapper>
            {/* Outside the form: disconnecting saves on its own. */}
            <SectionWrapper id="section-ai-apps">
              <ContentHeader variant={TextVariant.lg}>Connected AI apps</ContentHeader>
              <ConnectedAiApps />
            </SectionWrapper>
          </ContentArea>
        </SettingsContainer>
        <PageFooter>
          <Button
            variant={ButtonVariants.light}
            size={ButtonSizes.md}
            onClick={signUserOut}
            type="button"
            className="sign-out-button"
          >
            Sign out
          </Button>
          {hasUnsavedChanges && (
            <>
              <Button
                variant={ButtonVariants.light}
                size={ButtonSizes.md}
                onClick={handleCancel}
                type="button"
                disabled={loading}
                className="sign-out-button"
              >
                Cancel
              </Button>
              <Button
                variant={ButtonVariants.brand}
                size={ButtonSizes.md}
                onClick={handleSubmit(onSubmit)}
                type="submit"
                form="settings-form"
                disabled={loading}
                loading={loading}
              >
                Save
              </Button>
            </>
          )}
        </PageFooter>
      </SettingsCard>

      <AddLicensesDialog
        open={isAddLicensesOpen}
        onClose={() => setIsAddLicensesOpen(false)}
      />
      {/* HubSpot Property Creation Dialog */}
      <CreateHubSpotPropertyDialog
        open={createPropertyDialogOpen}
        onClose={() => setCreatePropertyDialogOpen(false)}
        onCreateProperty={handleCreateHubSpotProperty}
        fieldType={createPropertyFieldType}
        fieldLabel={createPropertyLabel}
        isCreating={isCreatingProperty}
      />
    </>
  );
};

export default Settings;
